import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Paper,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { addDocument, getCollection, updateDocument, deleteDocument } from '../../services/firestoreService';
import { useBranch } from '../../context/BranchContext';

const ConfigureGoals = () => {
  const { isAdmin, accessibleBranches, activeBranchId } = useBranch();
  const [goalName, setGoalName] = useState('');
  const [goalType, setGoalType] = useState('monthly');
  const [targetQuantity, setTargetQuantity] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [period, setPeriod] = useState('');
  const [branchId, setBranchId] = useState(activeBranchId || '');
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editingGoal, setEditingGoal] = useState(null);

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const goalsData = await getCollection('goals');
      setGoals(goalsData);
    } catch (error) {
      console.error('Error fetching goals:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const branch = accessibleBranches.find((b) => b.id === branchId);
      const goalData = {
        name: goalName,
        type: goalType,
        targetQuantity: targetQuantity ? parseFloat(targetQuantity) : 0,
        targetAmount: targetAmount ? parseFloat(targetAmount) : 0,
        period: period,
        branchId: branchId || '',
        branchName: branch?.name || '',
        createdAt: editingGoal ? editingGoal.createdAt : new Date(),
        updatedAt: new Date(),
        isActive: true
      };

      if (editingGoal) {
        await updateDocument('goals', editingGoal.id, goalData);
        setSnackbar({
          open: true,
          message: 'Meta actualizada exitosamente',
          severity: 'success'
        });
        setEditingGoal(null);
      } else {
        await addDocument('goals', goalData);
        setSnackbar({
          open: true,
          message: 'Meta creada exitosamente',
          severity: 'success'
        });
      }

      // Limpiar formulario
      setGoalName('');
      setGoalType('monthly');
      setTargetQuantity('');
      setTargetAmount('');
      setPeriod('');

      // Actualizar lista
      fetchGoals();
    } catch (error) {
      console.error('Error saving goal:', error);
      setSnackbar({
        open: true,
        message: 'Error al guardar la meta',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (goal) => {
    setEditingGoal(goal);
    setGoalName(goal.name);
    setGoalType(goal.type);
    setTargetQuantity(goal.targetQuantity ? goal.targetQuantity.toString() : '');
    setTargetAmount(goal.targetAmount ? goal.targetAmount.toString() : '');
    setPeriod(goal.period || '');
    setBranchId(goal.branchId || activeBranchId || '');
  };

  const handleCancelEdit = () => {
    setEditingGoal(null);
    setGoalName('');
    setGoalType('monthly');
    setTargetQuantity('');
    setTargetAmount('');
    setPeriod('');
    setBranchId(activeBranchId || '');
  };

  const handleDelete = async (goalId) => {
    if (window.confirm('¿Estás seguro de eliminar esta meta?')) {
      try {
        await deleteDocument('goals', goalId);
        setSnackbar({
          open: true,
          message: 'Meta eliminada exitosamente',
          severity: 'success'
        });
        fetchGoals();
      } catch (error) {
        console.error('Error deleting goal:', error);
        setSnackbar({
          open: true,
          message: 'Error al eliminar la meta',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getTypeLabel = (type) => {
    const types = {
      daily: 'Diaria',
      weekly: 'Semanal',
      monthly: 'Mensual',
      yearly: 'Anual'
    };
    return types[type] || type;
  };

  const formatPeriod = (period) => {
    if (!period) return '-';
    // Formato esperado: YYYY-MM
    const [year, month] = period.split('-');
    if (!year || !month) return period;
    
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    
    const monthIndex = parseInt(month, 10) - 1;
    return `${months[monthIndex]} ${year}`;
  };


  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Configurar Metas
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Define metas de desempeño para el equipo
      </Typography>
      <Paper sx={{ p: 3, mt: 3, bgcolor: 'background.default' }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Nombre de la Meta"
                value={goalName}
                onChange={(e) => setGoalName(e.target.value)}
                required
                placeholder="Ej: Meta mensual de solicitudes"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                select
                label="Tipo de Meta"
                value={goalType}
                onChange={(e) => setGoalType(e.target.value)}
                required
              >
                <MenuItem value="daily">Diaria</MenuItem>
                <MenuItem value="weekly">Semanal</MenuItem>
                <MenuItem value="monthly">Mensual</MenuItem>
                <MenuItem value="yearly">Anual</MenuItem>
              </TextField>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                helperText="Sucursal a la que aplica la meta"
              >
                {isAdmin && <MenuItem value=""><em>Todas las sucursales</em></MenuItem>}
                {accessibleBranches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Periodo Meta"
                type="month"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                helperText="Seleccione el mes de la meta"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Cantidad Objetivo"
                type="number"
                value={targetQuantity}
                onChange={(e) => setTargetQuantity(e.target.value)}
                placeholder="Ej: 100"
                helperText="Número de items/eventos"
                InputProps={{
                  inputProps: { min: 0 }
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Monto Objetivo ($)"
                type="number"
                value={targetAmount}
                onChange={(e) => setTargetAmount(e.target.value)}
                placeholder="Ej: 5000000"
                helperText="Valor monetario en pesos"
                InputProps={{
                  inputProps: { min: 0 }
                }}
              />
            </Grid>
            <Grid size={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ mr: 1 }}
              >
                {loading ? 'Guardando...' : editingGoal ? 'Actualizar Meta' : 'Crear Meta'}
              </Button>
              {editingGoal && (
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                >
                  Cancelar
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>
      {/* Lista de metas existentes */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Metas Configuradas
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Sucursal</TableCell>
                <TableCell>Periodo</TableCell>
                <TableCell align="right">Cantidad</TableCell>
                <TableCell align="right">Monto ($)</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {goals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No hay metas configuradas
                  </TableCell>
                </TableRow>
              ) : (
                goals.map((goal) => (
                  <TableRow key={goal.id}>
                    <TableCell>{goal.name}</TableCell>
                    <TableCell>
                      <Chip label={getTypeLabel(goal.type)} size="small" />
                    </TableCell>
                    <TableCell>{goal.branchName || (goal.branchId ? '—' : 'Todas')}</TableCell>
                    <TableCell>{formatPeriod(goal.period)}</TableCell>
                    <TableCell align="right">
                      {goal.targetQuantity ? goal.targetQuantity.toLocaleString('es-CL') : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {goal.targetAmount ? `$${goal.targetAmount.toLocaleString('es-CL')}` : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={goal.isActive ? 'Activa' : 'Inactiva'}
                        color={goal.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(goal)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(goal.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConfigureGoals;
